import axios from 'axios'
import { BillingErrorCode, ReferralStatus } from '@numeo/types-core'

// ============================================
// TYPES
// ============================================

export interface SendReferralInviteRequest {
    refereeEmail: string
}

export interface SendReferralInviteResponse {
    success: boolean
    data?: {
        referralId: string
        refereeEmail: string
        expiresAt: string
    }
    message?: string
    error?: string
}

export interface Referral {
    id: string
    refereeEmail: string
    status: ReferralStatus
    rewardGranted: boolean
    rewardGrantedAt?: string
    refereeSignedUpAt?: string
    refereeSubscribedAt?: string
    expiresAt: string
    createdAt: string
}

export interface ListReferralsResponse {
    success: boolean
    data?: Referral[]
    error?: string
    message?: string
}

// ============================================
// API FUNCTIONS
// ============================================

export const sendReferralInvite = async (data: SendReferralInviteRequest): Promise<SendReferralInviteResponse> => {
    try {
        const response = await axios.post<SendReferralInviteResponse>('/billing/referrals/invite', data)
        return response.data
    } catch (error) {
        if (axios.isAxiosError(error) && error.response?.data) {
            return error.response.data as SendReferralInviteResponse
        }
        return {
            success: false,
            error: BillingErrorCode.NETWORK_ERROR,
            message: 'Failed to send referral invite. Please try again.',
        }
    }
}

export interface ReferralLinkResponse {
    success: boolean
    data?: { link: string; code: string }
    message?: string
}

export const getReferralLink = async (): Promise<ReferralLinkResponse> => {
    try {
        const response = await axios.get<ReferralLinkResponse>('/billing/referral/link')
        return response.data
    } catch (error) {
        if (axios.isAxiosError(error) && error.response?.data) {
            return error.response.data as ReferralLinkResponse
        }
        return { success: false, message: 'Failed to get referral link.' }
    }
}

export const listReferrals = async (): Promise<ListReferralsResponse> => {
    try {
        const response = await axios.get<ListReferralsResponse>('/billing/referrals')
        return response.data
    } catch (error) {
        if (axios.isAxiosError(error) && error.response?.data) {
            return error.response.data as ListReferralsResponse
        }
        return {
            success: false,
            error: BillingErrorCode.NETWORK_ERROR,
            message: 'Failed to load referrals. Please try again.',
        }
    }
}
